import { useEffect, useMemo, useState } from "react";

import moment from "moment";
import Timeslot, { VariantTimeslot } from "okast-timeslot";

import useTimebounds from "./useTimebounds";
import { API_DATE_FORMAT } from "../constants/dateFormat";
import useFAST from "../contexts/FASTContext";
import { getComputedTimelineItemsKeyName } from "../utils/fast";
import { getIsClustered } from "../utils/timelineClusters";
import {
  defaultTimelineItems,
  getLocalStorageTimelineItems,
} from "../utils/timelineItems";

import type {
  ComputedTimelineItemsType,
  Media,
  TimelineContent,
  TimelineDataPartialFetched,
  TimelineMediaIdListByStartDate,
  TimeslotByStartDate,
  TimeslotsByStartDate,
} from "../types";

interface UseTimelineItemsReturn {
  currentTimelineMediaList: Media[];
  currentTimeslots: TimeslotByStartDate | undefined;
  isPartialDataOnCurrentDay: boolean | null;
  timelineContent: TimelineContent;
  timelineMediaIdListByStartDate: TimelineMediaIdListByStartDate;
  timelineDataPartialFetched: TimelineDataPartialFetched;
  computedItemsTypeKeyName: ComputedTimelineItemsType;
}

const useTimelineItems = (): UseTimelineItemsReturn => {
  const [timelineItems, setTimelineItems] = useState(defaultTimelineItems);

  const { state } = useFAST();

  const { isLoadingTimelineItems } = state;

  const { getTimebounds, getCurrentDay } = useTimebounds();

  const currentDay = getCurrentDay();

  const { startTime: timelineStartDate, endTime: timelineEndDate } =
    getTimebounds();

  const {
    timeslotsByStartDate,
    timelineMediaIdListByStartDate,
    timelineContent,
    timelineDataPartialFetched,
  } = timelineItems;

  const timelinePeriod = timelineEndDate - timelineStartDate;

  const isClustered = getIsClustered(timelinePeriod);

  const computedItemsTypeKeyName = getComputedTimelineItemsKeyName(isClustered);

  const previousDay = moment(currentDay)
    .subtract(1, "day")
    .format(API_DATE_FORMAT);
  const nextDay = moment(currentDay).add(1, "day").format(API_DATE_FORMAT);

  const mediaIdListOnCurrentDay = useMemo(
    () =>
      timelineMediaIdListByStartDate[currentDay]?.[computedItemsTypeKeyName] ??
      [],
    [timelineMediaIdListByStartDate, currentDay, computedItemsTypeKeyName],
  );

  const mediaIdListOnPreviousDay = useMemo(
    () =>
      timelineMediaIdListByStartDate[previousDay]?.[computedItemsTypeKeyName] ??
      [],
    [timelineMediaIdListByStartDate, previousDay, computedItemsTypeKeyName],
  );

  const mediaIdListOnNextDay = useMemo(
    () =>
      timelineMediaIdListByStartDate[nextDay]?.[computedItemsTypeKeyName] ?? [],
    [timelineMediaIdListByStartDate, nextDay, computedItemsTypeKeyName],
  );

  const currentTimeslots = useMemo(
    () => timeslotsByStartDate[currentDay]?.[computedItemsTypeKeyName],
    [timeslotsByStartDate, currentDay, computedItemsTypeKeyName],
  );

  const isPartialDataOnCurrentDay = useMemo(
    () =>
      timelineDataPartialFetched[currentDay]?.[computedItemsTypeKeyName] ??
      null,
    [timelineDataPartialFetched, currentDay, computedItemsTypeKeyName],
  );

  const currentTimelineMediaList = useMemo(
    () =>
      [
        ...mediaIdListOnCurrentDay,
        ...mediaIdListOnPreviousDay,
        ...mediaIdListOnNextDay,
      ].reduce((acc: Media[], mediaId) => {
        const media = timelineContent[mediaId];

        if (!media) return acc;
        acc.push(media);

        return acc;
      }, []),
    [
      mediaIdListOnCurrentDay,
      mediaIdListOnNextDay,
      mediaIdListOnPreviousDay,
      timelineContent,
    ],
  );

  useEffect(() => {
    const loadTimelineItemsFromStore = async (): Promise<void> => {
      const timelineItemsSaved = await getLocalStorageTimelineItems();

      const timeslotInstancesByStartDate = Object.entries(
        timelineItemsSaved?.timeslotsByStartDate || {},
      ).reduce<TimeslotsByStartDate>((acc, [date, timeslotsData]) => {
        const reducedDate = Object.entries(timeslotsData).reduce<
          Record<string, TimeslotByStartDate>
        >((acc2, [withWithout, currentSavedTimeslot]) => {
          const mainTimeslotInstance = new Timeslot(
            currentSavedTimeslot.timeslot,
          );

          const timeslotInstances = {
            timeslot: mainTimeslotInstance,
            variantTimeslots: currentSavedTimeslot.variantTimeslots.map(
              (variantTimeslot) =>
                new VariantTimeslot(
                  mainTimeslotInstance,
                  variantTimeslot.usedSlots,
                  variantTimeslot.id,
                ),
            ),
          };

          return {
            ...acc2,
            [withWithout]: timeslotInstances,
          };
        }, {});

        return {
          ...acc,
          [date]: reducedDate,
        };
      }, {});

      const timelineItemsWithInstances = {
        ...timelineItemsSaved,
        timeslotsByStartDate: timeslotInstancesByStartDate,
      };

      setTimelineItems(timelineItemsWithInstances || defaultTimelineItems);
    };
    loadTimelineItemsFromStore();
  }, [currentDay, isClustered, isLoadingTimelineItems]);

  return {
    currentTimelineMediaList,
    currentTimeslots,
    isPartialDataOnCurrentDay,
    timelineContent,
    timelineMediaIdListByStartDate,
    timelineDataPartialFetched,
    computedItemsTypeKeyName,
  };
};

export default useTimelineItems;
